import React, { useState, memo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useArtistBySlug } from '../hooks/useArtists';
import { useArtworksByArtist } from '../hooks/useArtworks';
import { useArtists } from '../hooks/useArtists';
import Loading from '../components/Loading';
import ErrorDisplay from '../components/ErrorDisplay';
import YouTubePlayer from '../components/YouTubePlayer';
import { Artwork } from '../types';

// Popup component for artwork enlargement
const ArtworkPopup: React.FC<{
  artwork: Artwork;
  isOpen: boolean;
  onClose: () => void;
}> = memo(({ artwork, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-full">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-colors"
          aria-label="סגור"
        >
          ×
        </button>
        <img
          src={artwork.image_url}
          alt={artwork.title || 'יצירה'}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        {artwork.title && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-4">
            <h3 className="text-lg font-medium">{artwork.title}</h3>
          </div>
        )}
      </div>
    </div>
  );
});

ArtworkPopup.displayName = 'ArtworkPopup';

const ArtistPage: React.FC = memo(() => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [popupArtwork, setPopupArtwork] = useState<Artwork | null>(null);

  // Decode the slug for Hebrew characters
  const decodedSlug = slug ? decodeURIComponent(slug) : '';
  
  console.log('ArtistPage - URL slug:', slug, 'decoded:', decodedSlug);

  // Fetch artist data
  const { 
    data: artistData, 
    isLoading: artistLoading, 
    error: artistError,
    refetch: refetchArtist 
  } = useArtistBySlug(decodedSlug);
  
  console.log('ArtistPage - API response:', { artistData, artistLoading, artistError });

  // Fetch artworks for this artist
  const artistId = artistData?.artist?.id;
  const {
    artworks,
    isLoading: artworksLoading,
    error: artworksError,
    refetch: refetchArtworks
  } = useArtworksByArtist(artistId || '');

  // Fetch all artists for navigation
  const { artists: allArtists } = useArtists({ limit: 100 });

  // Navigation handlers
  const handleNavigateBack = useCallback(() => {
    navigate('/exhibition');
  }, [navigate]);

  const getCurrentArtistIndex = useCallback(() => {
    if (!artistData?.artist || !allArtists.length) return -1;
    return allArtists.findIndex(artist => artist.id === artistData.artist.id);
  }, [artistData?.artist, allArtists]);

  const goToPreviousArtist = useCallback(() => {
    const currentIndex = getCurrentArtistIndex();
    if (currentIndex > 0) {
      const previousArtist = allArtists[currentIndex - 1];
      navigate(`/artist/${previousArtist.slug}`);
      window.scrollTo(0, 0);
    }
  }, [getCurrentArtistIndex, allArtists, navigate]);

  const goToNextArtist = useCallback(() => {
    const currentIndex = getCurrentArtistIndex();
    if (currentIndex >= 0 && currentIndex < allArtists.length - 1) {
      const nextArtist = allArtists[currentIndex + 1];
      navigate(`/artist/${nextArtist.slug}`);
      window.scrollTo(0, 0);
    }
  }, [getCurrentArtistIndex, allArtists, navigate]);

  const handleArtworkClick = useCallback((artwork: Artwork) => {
    setPopupArtwork(artwork);
  }, []);

  const closePopup = useCallback(() => {
    setPopupArtwork(null);
  }, []);

  // Loading state
  if (artistLoading || artworksLoading) {
    return <Loading message="טוען פרטי אמן..." fullScreen />;
  }

  // Error state
  if (artistError || artworksError) {
    return (
      <ErrorDisplay 
        error={artistError || artworksError || 'שגיאה בטעינת נתוני האמן'}
        onRetry={() => {
          refetchArtist();
          refetchArtworks();
        }}
        fullScreen
        title="שגיאה בטעינת עמוד האמן"
      />
    );
  }

  // Artist not found
  if (!artistData?.artist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">אמן לא נמצא</h2>
          <button 
            onClick={handleNavigateBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            חזרה לתערוכה
          </button>
        </div>
      </div>
    );
  }

  const currentArtist = artistData.artist;

  // Find artworks by display position
  const topArtwork = artworks.find(artwork => artwork.display_position === 'top');
  const bottomArtwork = artworks.find(artwork => artwork.display_position === 'bottom');

  const currentIndex = getCurrentArtistIndex();
  const canGoToPrevious = currentIndex > 0;
  const canGoToNext = currentIndex >= 0 && currentIndex < allArtists.length - 1;

  return (
    <div className="min-h-screen bg-white" dir="rtl">

      {/* Top Artwork - Image or Video */}
      {topArtwork && (
        <div className="artist-primary-image-container">
          <div className="artist-primary-image">
            {topArtwork.youtube_url ? (
              <YouTubePlayer
                videoUrl={topArtwork.youtube_url}
                title={topArtwork.title || `יצירה של ${currentArtist.name}`}
              />
            ) : topArtwork.image_url ? (
              <img
                src={topArtwork.image_url}
                alt={topArtwork.title || `יצירה של ${currentArtist.name}`}
                onClick={() => handleArtworkClick(topArtwork)}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.unsplash.com/photo-1578321272176-b7bbc0679853?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80';
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {/* Two-Column Content Below Primary Image */}
      <div className="artist-content-container">
        <div className="artist-content-layout">
          {/* Left Column - Bottom Artwork (Image or Video) */}
          <div className="artist-secondary-image-column">
            {bottomArtwork ? (
              <div className="artist-secondary-image">
                {bottomArtwork.youtube_url ? (
                  <YouTubePlayer
                    videoUrl={bottomArtwork.youtube_url}
                    title={bottomArtwork.title || `יצירה נוספת של ${currentArtist.name}`}
                  />
                ) : bottomArtwork.image_url ? (
                  <img
                    src={bottomArtwork.image_url}
                    alt={bottomArtwork.title || `יצירה נוספת של ${currentArtist.name}`}
                    onClick={() => handleArtworkClick(bottomArtwork)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://images.unsplash.com/photo-1578321272176-b7bbc0679853?ixlib=rb-4.0.3&auto=format&fit=crop&w=717&h=755&q=80';
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <div className="artist-secondary-image artist-secondary-placeholder">
                <div className="placeholder-text">אין תמונה נוספת</div>
              </div>
            )}
          </div>

          {/* Right Column - Text Content */}
          <div className="artist-text-column">
            <div className="artist-text-content">
              {/* Project Title */}
              <h2 className="artist-project-title">
                {currentArtist.project_title || 'PROJECT TITLE'}
              </h2>
              
              {/* Artist Name */}
              <h3 className="artist-name-subtitle">
                {currentArtist.name}
              </h3>
              
              {/* Artist Statement */}
              <div className="artist-statement">
                {currentArtist.statement ? (
                  currentArtist.statement.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))
                ) : (
                  <p>אין הצהרת אמן זמינה</p>
                )}
              </div>
              
              {/* PDF Download Button - Only show if PDF exists */}
              {currentArtist.pdf_url && (
                <div className="artist-pdf-download">
                  <a
                    href={currentArtist.pdf_url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pdf-download-button"
                  >
                    <svg className="pdf-icon-svg" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span className="pdf-text">לעבודת החקר המלאה</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Navigation Arrows */}
      <div className="artist-navigation-container">
        <div className="artist-navigation-arrows">
          <button
            onClick={goToPreviousArtist}
            disabled={!canGoToPrevious}
            className={`artist-nav-button artist-nav-previous ${
              canGoToPrevious ? 'active' : 'disabled'
            }`}
          >
            <svg className="nav-arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>למציג.ה הקודמ.ת</span>
          </button>

          <button
            onClick={goToNextArtist}
            disabled={!canGoToNext}
            className={`artist-nav-button artist-nav-next ${
              canGoToNext ? 'active' : 'disabled'
            }`}
          >
            <span>למציג.ה הבא.ה</span>
            <svg className="nav-arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Artwork Popup */}
      {popupArtwork && (
        <ArtworkPopup
          artwork={popupArtwork}
          isOpen={!!popupArtwork}
          onClose={closePopup}
        />
      )}
    </div>
  );
});

ArtistPage.displayName = 'ArtistPage';

export default ArtistPage;